// RECALL-KB-LINKS, DOC-LINKS: the Related tab beside a doc (`pages.related`): the docs that link to
// it, links in it that point nowhere and its tags (from CompanyOS, for any doc, memory on or off)
// and, when memory is on, the docs that name it without linking and, for a company admin, the
// versions memory holds with their review and rollback. Short labels; what a thing means is behind
// an (i). Only a task's body has no tab (`pageRelatedQuery` reads `off` as null). Review and rollback are the
// person's act in memory, which the memory service does not take yet: the icons are there and
// say "Waiting on memory service", and nothing is sent.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon, FileTextIcon, Undo2Icon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { ago } from "./doc-kinds";
import { invalidateWork, pageRelatedQuery, workKeys } from "./work-queries";

type Related = NonNullable<Awaited<ReturnType<Api["client"]["pages"]["related"]>>>;
type Version = NonNullable<Related["versions"]>[number];
type Act = "clear" | "reject" | "rollback";

const WAITING = "Waiting on memory service";

/** The doc's Related data; null when memory says nothing (the tab is then not shown). */
export function useDocRelated(api: Api, co: string, pageId: string) {
  return useQuery(pageRelatedQuery(api, co, pageId));
}

function Links({
  company,
  title,
  rows,
}: {
  company: CompanyAccess;
  title: string;
  rows: Related["backlinks"];
}) {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title} data-slot="related-section">
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">{title}</h3>
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
        {rows.map((r) => (
          <li key={r.page_id}>
            <Link
              to="/c/$slug/work/p/$pageId"
              params={{ slug: company.slug, pageId: r.page_id }}
              search={keepFocus}
              className="flex items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
            >
              <FileTextIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-faint" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{r.title}</span>
                {r.snippet ? (
                  <span className="line-clamp-2 text-xs text-muted-foreground">{r.snippet}</span>
                ) : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const ACT_LABEL: Record<Act, string> = {
  clear: "Clear",
  reject: "Reject",
  rollback: "Roll back",
};

function VersionRow({
  v,
  waiting,
  onAct,
}: {
  v: Version;
  waiting: boolean;
  onAct: (act: Act, v: Version) => void;
}) {
  const icon = (act: Act, Icon: typeof CheckIcon, off: string | null) => (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`${ACT_LABEL[act]} v${v.version}`}
            disabled={waiting || off !== null}
            onClick={() => onAct(act, v)}
          />
        }
      >
        <Icon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{waiting ? WAITING : (off ?? ACT_LABEL[act])}</TooltipContent>
    </Tooltip>
  );
  return (
    <li className="flex items-center gap-2 rounded-md px-2 py-1 text-sm" data-version={v.version}>
      <span className="font-medium tabular-nums">v{v.version}</span>
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        {v.created_at ? formatDateTime(v.created_at) : ""}
        {v.current ? " · current" : ""}
      </span>
      {v.held ? <Badge variant="warning">Held</Badge> : null}
      <span className="flex shrink-0 items-center">
        {v.held ? (
          <>
            {icon("clear", CheckIcon, null)}
            {icon("reject", XIcon, null)}
          </>
        ) : v.current ? null : (
          icon("rollback", Undo2Icon, v.doc_version === null ? "Not in Docs history" : null)
        )}
      </span>
    </li>
  );
}

function MemoryVersions({
  api,
  company,
  pageId,
  pageVersion,
  data,
}: {
  api: Api;
  company: CompanyAccess;
  pageId: string;
  pageVersion: number;
  data: Related;
}) {
  const co = company.id;
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<{ act: Act; v: Version } | null>(null);
  const waiting = data.review !== "ready";
  const run = useMutation({
    mutationFn: async ({ act, v }: { act: Act; v: Version }) => {
      // memory takes the act first; only when it did is a rollback also a Docs rollback, so the
      // next sync agrees (a waiting memory leaves the doc as it is)
      const r = await api.client.pages.memoryReview({
        params: { co, id: pageId },
        body: { action: act, version: v.version },
      });
      if (
        r.result === "done" &&
        act === "rollback" &&
        v.doc_version !== null &&
        v.doc_version !== pageVersion
      )
        await api.client.pages.restore({
          params: { co, id: pageId },
          body: { version: v.doc_version },
        });
      return r;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.page(co, pageId) });
    },
    onSuccess: async (r, { act, v }) => {
      setAsking(null);
      if (r.result === "waiting") toast.add({ title: WAITING, type: "info" });
      else toast.add({ title: `${ACT_LABEL[act]}: v${v.version} done`, type: "success" });
      await invalidateWork(queryClient, co);
      await queryClient.invalidateQueries({ queryKey: workKeys.page(co, pageId) });
    },
    onError: (e) => {
      setAsking(null);
      toast.add({
        title: "Not done",
        description: e instanceof ApiError ? (e.problem?.detail ?? e.problem?.title) : "Try again.",
        type: "error",
      });
    },
  });
  return (
    <section aria-label="In memory" data-slot="related-versions">
      <h3 className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        In memory
        <InfoTip label="About versions in memory">
          <p>
            The versions memory holds of this doc. Clear or reject a held one; roll back to an
            earlier one.
          </p>
        </InfoTip>
        {waiting ? (
          <Badge variant="outline" className="ml-auto" data-slot="related-waiting">
            {WAITING}
          </Badge>
        ) : null}
      </h3>
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
        {(data.versions ?? []).map((v) => (
          <VersionRow
            key={v.version}
            v={v}
            waiting={waiting}
            onAct={(act, x) => setAsking({ act, v: x })}
          />
        ))}
      </ul>
      <Dialog open={asking !== null} onOpenChange={(o) => (o ? null : setAsking(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {asking ? `${ACT_LABEL[asking.act]} v${asking.v.version}?` : ""}
            </DialogTitle>
            <DialogDescription>
              {asking?.act === "rollback"
                ? "The doc goes back to this version too."
                : asking?.act === "clear"
                  ? "It goes live in memory."
                  : "It stays out of memory."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAsking(null)}>
              Cancel
            </Button>
            <Button
              disabled={run.isPending || !asking}
              onClick={() => asking && run.mutate(asking)}
            >
              {asking ? ACT_LABEL[asking.act] : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

type About = NonNullable<Awaited<ReturnType<Api["client"]["spaces"]["docsMemory"]>>>;

/** "About these docs": the Docs home's line for the space it is filtered to (nothing while null). */
export function AboutDocs({ about }: { about: About }) {
  return (
    <p
      className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-subtle py-2 text-xs text-muted-foreground"
      data-slot="docs-about"
    >
      <span className="font-medium text-foreground">About these docs</span>
      <InfoTip label="About these docs">
        <p>What company memory holds of this space. Only docs you can open are counted.</p>
      </InfoTip>
      <span>
        {about.docs} {about.docs === 1 ? "doc" : "docs"} in memory
        {about.parts !== null ? ` · ${about.parts} parts` : ""}
        {about.synced_at ? ` · synced ${ago(about.synced_at)}` : ""}
      </span>
      {about.unresolved.length ? (
        <span className="inline-flex items-center gap-0.5" data-slot="docs-about-unresolved">
          <Badge variant="warning">
            {about.unresolved.length} {about.unresolved.length === 1 ? "link" : "links"} with no doc
          </Badge>
          <InfoTip label="Links with no doc">
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {about.unresolved.map((u) => (
                <li key={u.target}>
                  <span className="font-medium text-foreground">{u.target}</span>
                  {" in "}
                  {u.pages.map((p) => p.title).join(", ")}
                </li>
              ))}
            </ul>
          </InfoTip>
        </span>
      ) : null}
    </p>
  );
}

/** The Related tab's body. */
export function RelatedPanel({
  api,
  company,
  pageId,
  pageVersion,
  data,
}: {
  api: Api;
  company: CompanyAccess;
  pageId: string;
  pageVersion: number;
  data: Related;
}) {
  const none =
    data.backlinks.length === 0 &&
    data.mentions.length === 0 &&
    data.unresolved.length === 0 &&
    data.tags.length === 0;
  return (
    <div className="flex flex-col gap-4" data-slot="page-related">
      <p className="m-0 flex items-center gap-1 text-xs text-muted-foreground">
        Links between docs
        <InfoTip label="About related">
          <p>
            Read from the docs themselves. Plain mentions and memory versions show when memory is
            on. Only docs you can open are listed.
          </p>
        </InfoTip>
      </p>
      <Links company={company} title="Linked from" rows={data.backlinks} />
      <Links company={company} title="Mentioned in" rows={data.mentions} />
      {data.unresolved.length ? (
        <section aria-label="Links with no doc" data-slot="related-section">
          <h3 className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
            Links with no doc
            <InfoTip label="About links with no doc">
              <p>Links in this doc to a doc that does not exist (or was deleted).</p>
            </InfoTip>
          </h3>
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
            {data.unresolved.map((t) => (
              <li key={t}>
                <Badge variant="warning">{t}</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {data.tags.length ? (
        <section aria-label="Tags" data-slot="related-section">
          <h3 className="mb-1 text-xs font-medium text-muted-foreground">Tags</h3>
          <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
            {data.tags.map((t) => (
              <li key={t}>
                <Badge variant="neutral">#{t}</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {none ? <p className="m-0 text-sm text-muted-foreground">Nothing linked yet.</p> : null}
      {data.versions ? (
        <MemoryVersions
          api={api}
          company={company}
          pageId={pageId}
          pageVersion={pageVersion}
          data={data}
        />
      ) : null}
    </div>
  );
}
